import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Category } from "../../model/asset.model";
import { AssetCard } from "../../components/AssetCard/AssetCard";
import {
  assetMatches,
  findUniqueCategoryBySearchQuery,
} from "../BrowseAllPage/filterCategories";
import { useRealCatalog } from "../../hooks/useRealCatalog";
import styles from "./CategoryPage.module.css";
import { EmptySearchState } from "../../components/EmptySearchState/EmptySearchState";

export function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { categories, loading } = useRealCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q")?.trim() ?? "";
  const [bannerScrolled, setBannerScrolled] = useState(false);

  const byRoute = useMemo(
    () => categories.find((c) => c.id === categoryId) ?? null,
    [categoryId, categories]
  );

  const uniqueFromQuery = useMemo(
    () => findUniqueCategoryBySearchQuery(categories, q),
    [categories, q]
  );

  /** Pool for listing: URL category, or if invalid URL + unique search match, that category */
  const categoryForAssets: Category | null = useMemo(() => {
    if (byRoute) return byRoute;
    if (q && uniqueFromQuery) return uniqueFromQuery;
    return null;
  }, [byRoute, q, uniqueFromQuery]);

  /**
   * When search uniquely identifies a category (e.g. "Drama"), show that name in the banner.
   * Otherwise show the category you are browsing.
   */
  const bannerTitle = useMemo(() => {
    if (q && uniqueFromQuery) return uniqueFromQuery.name;
    return byRoute?.name ?? "Category";
  }, [q, uniqueFromQuery, byRoute]);

  const assets = useMemo(() => {
    const pool = categoryForAssets?.assets ?? [];
    if (!q) return pool;
    return pool.filter((a) => assetMatches(a, q));
  }, [categoryForAssets, q]);

  const handlePrimaryTagSearch = useCallback(
    (label: string) => {
      setSearchParams({ q: label.trim() }, { replace: true });
    },
    [setSearchParams]
  );

  const handleMainScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      setBannerScrolled(e.currentTarget.scrollTop > 8);
    },
    []
  );

  useEffect(() => {
    setBannerScrolled(false);
  }, [categoryId, q]);

  const headerClassName = [
    styles.header,
    bannerScrolled ? styles.headerScrolled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.shell}>
      <header className={headerClassName}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className={styles.title}>{bannerTitle}</h1>
        <div className={styles.headerSpacer} aria-hidden />
      </header>

      <main className={styles.grid} onScroll={handleMainScroll}>
        {!categoryForAssets ? (
          loading ? (
            <p className={styles.empty}>Loading category...</p>
          ) : (
            <p className={styles.empty}>Category not found.</p>
          )
        ) : assets.length === 0 && q ? (
          <EmptySearchState
            onClearSearch={() => setSearchParams({}, { replace: true })}
          />
        ) : assets.length === 0 ? (
          <p className={styles.empty}>No experiences in this category.</p>
        ) : (
          assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onPrimaryTagSearch={handlePrimaryTagSearch}
            />
          ))
        )}
      </main>
    </div>
  );
}
